import React, { Component, Fragment } from "react";
import styled from "styled-components";
import {
    StripeProvider,
    Elements,
    CardElement,
    injectStripe,
} from "react-stripe-elements";

import Api from "js/core/api";
import Button from "js/react/components/Button2";
import { ShowErrorDialog } from "js/react/components/Dialogs/BaseDialog";
import PromoField from "js/react/views/UserOptions/components/PromoField";
import { FlexBox } from "js/react/components/LayoutGrid";
import { Gap10, Gap20, Gap30 } from "js/react/components/Gap";
import withStripe from "js/react/views/UserOptions/Billing/withStripe";
import getLogger, { LogGroup } from "js/core/logger";

import "css/billing.scss";

const logger = getLogger(LogGroup.BILLING);

const ButtonContainer = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
`;

async function handleStripeErrors(stripeCall) {
    const { error, ...rest } = await stripeCall();
    if (error) {
        throw new Error(error.message);
    }

    return rest;
}

class PaymentFormInner extends Component {
    state = {
        promotion_code: null,
        submitting: false,
    };

    handleOnChange(state) {
        this.setState(state);
    }

    createSubscription = async payment_method => {
        const { productName, billingInterval, productPrice, seatCount, customerType, teamName, members, message, onSuccess, stripe, organizationId, isReactivating } = this.props;
        const { promotion_code } = this.state;

        const apiResponse = await Api.subscriptions.post({
            payment_method_id: payment_method.id,
            invoice_id: this.state.invoice_id,
            product_name: productName,
            product_type: billingInterval,
            product_price: productPrice,
            quantity: seatCount || 1,
            customer_type: customerType,
            team_name: teamName,
            organization_id: organizationId,
            promotion_code
        });

        const finalize = async () => {
            const { body: updatedPaymentResult } = await Api.subscriptions.put({
                customerType,
                members: members ?? apiResponse.members,
                message,
                orgId: apiResponse.org?.id,
                type: "finalize",
                isReactivating
            });

            onSuccess({ ...apiResponse, ...updatedPaymentResult });
        };

        if (apiResponse.status === "succeeded") {
            await finalize();
            return;
        }

        if (apiResponse.status === "requires_setup_action") {
            await handleStripeErrors(() => stripe.confirmCardSetup(apiResponse.client_secret, {
                payment_method: payment_method.id
            }));

            await finalize();
            return;
        }

        if (apiResponse.status === "requires_action") {
            await handleStripeErrors(() => stripe.confirmCardPayment(apiResponse.client_secret, {
                payment_method: payment_method.id
            }));

            await finalize();
            return;
        }

        if (apiResponse.status === "requires_payment_method") {
            // Payment method missing/invalid, so we clear the exisitng payment method to show the form with Elements
            await Api.subscriptions.delete({ subscriptionId: apiResponse.subscription_id, forceDelete: true });

            this.setState({
                error: "Payment method is missing or invalid. Please try another card."
            });
            return;
        }

        throw new Error("Unexpected error occurred");
    };

    checkAndSubmit = () => {
        const { checkIfFormShouldSubmit } = this.props;

        if (checkIfFormShouldSubmit) {
            if (checkIfFormShouldSubmit()) {
                this.handleSubmit();
            }
            return;
        }

        this.handleSubmit();
    }

    handleSubmit = async () => {
        const { stripe, onBeforeCharge, onFailure } = this.props;
        const { submitting } = this.state;

        if (submitting) {
            return;
        }

        this.setState({ submitting: true });

        onBeforeCharge();

        try {
            const { paymentMethod } = await handleStripeErrors(() => stripe.createPaymentMethod("card"));

            const { clientSecret } = await Api.preCheckout.post();

            await handleStripeErrors(() => stripe.confirmCardSetup(clientSecret, {
                payment_method: paymentMethod.id
            }));

            await this.createSubscription(paymentMethod);
        } catch (err) {
            logger.error(err, "[PaymentForm] Error creating subscription");

            ShowErrorDialog({
                error: "An error occurred while creating this transaction",
                message: (
                    <Fragment>
                        <p style={{ marginTop: 0 }}>
                            <strong>Error:</strong> {err.message}
                        </p>
                        <p>We apologize for the inconvenience. Please contact us at support@beautiful.ai.</p>
                    </Fragment>
                )
            });

            onFailure(err);

            this.setState({ submitting: false });
        }
    };

    handleFetchPromo = async code => {
        const { productName, amount, hasTakenTrial, onPromoChanged } = this.props;

        const promo = await Api.promos.get({ code });

        // check restrictions
        if (promo.appliesTo && !promo.appliesTo.includes(productName)) {
            throw new Error(`This coupon can't be applied to a ${productName} plan`);
        }

        if (promo.minimumAmount && amount * 100 < promo.minimumAmount) {
            throw new Error(`This promo can't be applied to bills of less than $${promo.minimumAmount / 100}`);
        }

        if (promo.firstTimeTransaction && hasTakenTrial) {
            throw new Error("This promo is only for new users");
        }

        if (promo.error) {
            throw new Error(promo.error);
        }

        this.setState({ promotion_code: promo.id });
        onPromoChanged(promo);

        return promo;
    }

    handleClearPromo = () => {
        const { onPromoChanged } = this.props;

        this.setState({ promotion_code: null });

        onPromoChanged(null);
    }

    render() {
        const {
            upgradeBtnDisabled,
            buttonStyle,
            productName,
            billingInterval,
            submitLabel,
            buttonRedesign = false,
            onCancel,
            cancelLabel = "Cancel",
            gapElement = <Gap20 />,
            paymentFormTitle = "label",
            paymentDetails = null

        } = this.props;
        const { error, submitting } = this.state;

        return (
            <Fragment>
                <div className={paymentFormTitle}>Payment details</div>
                <Gap10 />
                <div className="payment-details">
                    <CardElement
                        onChange={event => {
                            if (event.error) {
                                this.setState({
                                    error: event.error.message
                                });
                            } else {
                                this.setState({ error: null });
                            }
                        }}
                        onFocus={() => {
                            document.querySelector(".payment-details").classList.add("focused");
                        }}
                        onBlur={() => {
                            document.querySelector(".payment-details").classList.remove("focused");
                        }}
                    />
                    {error && <div className="card-error">{error}</div>}
                </div>
                <Fragment>
                    {gapElement}
                    <PromoField
                        fetchPromo={this.handleFetchPromo}
                        fetchKey={`${productName}-${billingInterval}`}
                        onClear={this.handleClearPromo}
                    />
                </Fragment>
                {paymentDetails && <Fragment>
                    <Gap10 />
                    {paymentDetails}
                </Fragment>}
                {buttonRedesign
                    ? <>
                        <Gap30 />
                        <ButtonContainer>
                            <Button
                                style={{
                                    background: "transparent",
                                    color: "#666",
                                    boxShadow: "none",
                                }}
                                variant="contained"
                                disabled={submitting}
                                label={cancelLabel}
                                onClick={onCancel}
                            />
                            <Button
                                style={{
                                    width: "auto"
                                }}
                                disabled={upgradeBtnDisabled || submitting}
                                onClick={this.checkAndSubmit}
                                label={submitLabel}
                                id="pay"
                            />
                        </ButtonContainer>
                    </> : <>
                        <Button
                            style={buttonStyle || {}}
                            disabled={upgradeBtnDisabled || submitting}
                            onClick={this.checkAndSubmit}
                            label={submitLabel}
                            id="pay"
                        />
                        {onCancel && <FlexBox center>
                            <Button
                                style={{
                                    background: "transparent",
                                    color: "#11a9e2",
                                    marginTop: "10px",
                                }}
                                disabled={submitting}
                                label={cancelLabel}
                                onClick={onCancel}
                            />
                        </FlexBox>}
                    </>}
            </Fragment>
        );
    }
}

const InjectedPaymentFormInner = injectStripe(PaymentFormInner);

class PaymentForm extends Component {
    render() {
        const { stripe, ...props } = this.props;

        return (
            <StripeProvider stripe={stripe}>
                <Elements fonts={[{ cssSrc: "https://fonts.googleapis.com/css?family=Source+Sans+Pro&display=swap" }]}>
                    <div id="checkout_form">
                        <InjectedPaymentFormInner {...props} />
                    </div>
                </Elements>
            </StripeProvider>
        );
    }
}

export default withStripe(PaymentForm);
